import { useEffect, useState } from 'react';
import {
  Drawer,
  Center,
  Spinner,
  Box,
  Text,
  Heading,
  Stack,
  Flex,
  IconButton,
  Spacer,
  Icon,
  Grid,
  GridItem,
  Portal,
} from '@chakra-ui/react';
import {
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import { Prose } from '@/components/ui/prose';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import ContentDrawer from '@/pages/contents/content-drawer';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import useIsCurrentUser from '@/lib/hooks/use-is-current-user';
import api, { baseApi } from '@/utils/api';
import { Event, Content } from '@/lib/types';

interface EventProp extends Event {
  contents: {
    reference_id: string;
    name: string;
    description: string;
    content_type_name: string;
  }[];
}

interface Props {
  isOpen: boolean;
  onClose(): void;
  referenceId: string;
  reload(): void;
  onEdit?(): void;
}

export default function EventDrawer({ isOpen, onClose, referenceId, reload, onEdit }: Props) {
  const [item, setItem] = useState<EventProp | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedContent, setSelectedContent] = useState<Content>();
  const contentDrawerModal = useModalToggle();
  const currentUser = useIsCurrentUser();

  useEffect(() => {
    const getEvent = async () => {
      setItem(null);
      setIsLoading(true);

      try {
        const response = await api.get(`calendars/reference/${referenceId}`);
        const item = await response.data;
        if (!item) {
          return;
        }
        setItem(item);
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to load event',
        });
      } finally {
        setIsLoading(false);
      }
    };

    getEvent();
  }, [referenceId]);

  async function handleChangeStatus(item: Event, status: 'draft' | 'published') {
    try {
      const promise = api.patch(`/calendars/status/${item.id}/${status}`);

      toaster.promise(promise, {
        success: {
          title: `The event was ${status === 'published' ? 'shared to' : 'hidden from'} workspace`,
        },
        error: {
          title: `Failed to ${status === 'published' ? 'share to' : 'hide from'} workspace`,
        },
        loading: {
          title: `${status === 'published' ? 'Sharing to workspace' : 'Hiding from workspace'}...`,
          description: 'Please wait',
        },
      });

      await promise;

      setTimeout(() => {
        onClose();
        reload();
      }, 1000);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to change status',
      });
      setIsLoading(false);
    }
  }

  function handleDelete(item: Event) {
    setSelectedItemId(item.id);
    setShowDeleteModal(true);
  }

  async function onDelete() {
    setIsLoading(true);

    try {
      await api.delete(`calendars/${selectedItemId}`);

      setShowDeleteModal(false);
      setSelectedItemId(null);
      onClose();
      reload();
      toaster.create({ title: 'Event removed', type: 'success' });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to delete event',
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleView = async (referenceId: string) => {
    try {
      const response = await baseApi.get(`public/content/${referenceId}`);
      const content = await response.data.data;
      if (!content) {
        return;
      }
      setSelectedContent(content);
      contentDrawerModal.open();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch content',
      });
    }
  };

  return (
    <Drawer.Root
      id={'view-event-drawer'}
      size='xl'
      placement='end'
      open={isOpen}
      onOpenChange={onClose}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Body>
              {isLoading && (
                <Center py={10}>
                  <Spinner size='xl' color='brand.500' />
                </Center>
              )}

              {!isLoading && item && (
                <Box pb={6} className='print-container'>
                  <Flex justifyContent='space-between' alignItems='center' mb={6} mt={2}>
                    <Heading as={'h1'} size={'2xl'}>
                      {item.title}
                    </Heading>
                    <Spacer />
                    <Flex gap={2}>
                      {currentUser.id === item.created_by && (
                        <>
                          <Tooltip content={'Edit'}>
                            <IconButton
                              variant={'ghost'}
                              aria-label={'Edit'}
                              size={'sm'}
                              onClick={onEdit}
                            >
                              <Icon as={PencilIcon} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            content={`${item.status === 'published' ? 'Hide from Workspace' : 'Share to Workspace'}`}
                          >
                            <IconButton
                              variant={'ghost'}
                              aria-label={`${item.status}`}
                              size={'sm'}
                              onClick={() =>
                                handleChangeStatus(
                                  item,
                                  item.status === 'published' ? 'draft' : 'published',
                                )
                              }
                            >
                              <Icon as={item.status === 'published' ? EyeIcon : EyeSlashIcon} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip content={'Delete'}>
                            <IconButton
                              variant={'ghost'}
                              aria-label={'Delete'}
                              size={'sm'}
                              onClick={() => handleDelete(item)}
                            >
                              <Icon as={TrashIcon} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      <Tooltip content={'Close'}>
                        <IconButton
                          variant={'ghost'}
                          aria-label={'Close'}
                          size={'sm'}
                          onClick={onClose}
                        >
                          <Icon as={XMarkIcon} />
                        </IconButton>
                      </Tooltip>
                    </Flex>
                  </Flex>

                  {(item.event_date || item.event_time) && (
                    <Box
                      mb={4}
                      p={3}
                      borderLeft='4px solid'
                      borderColor='brand.500'
                      bg='gray.50'
                      borderRadius='md'
                    >
                      <Grid templateColumns='repeat(2, 1fr)' gap={4}>
                        {item.event_date && (
                          <GridItem>
                            <Flex align='center' gap={4}>
                              <Icon as={CalendarIcon} color='gray.500' w={5} h={5} />
                              <Box>
                                <Text fontSize='xs' color='gray.500' fontWeight='medium'>
                                  Date
                                </Text>
                                <Text fontWeight='medium'>
                                  {format(new Date(item.event_date), 'MMMM d, yyyy')}
                                </Text>
                              </Box>
                            </Flex>
                          </GridItem>
                        )}

                        {item.event_time && (
                          <GridItem>
                            <Flex align='center' gap={4}>
                              <Icon as={ClockIcon} color='gray.500' w={5} h={5} />
                              <Box>
                                <Text fontSize='xs' color='gray.500' fontWeight='medium'>
                                  Time
                                </Text>
                                <Text fontWeight='medium'>
                                  {format(new Date(`2000-01-01T${item.event_time}`), 'h:mm a')}
                                </Text>
                              </Box>
                            </Flex>
                          </GridItem>
                        )}
                      </Grid>
                    </Box>
                  )}

                  <Stack gap={4}>
                    <Box>
                      <Prose size='md'>
                        <div dangerouslySetInnerHTML={{ __html: item?.description }} />
                      </Prose>
                    </Box>
                    <Box>
                      {item.contents && item.contents.length > 0 && (
                        <Stack mt={3}>
                          {item.contents.map((content) => (
                            <Box
                              key={content.reference_id}
                              py={1}
                              px={4}
                              bg={'gray.100'}
                              borderRadius={'md'}
                              transition='all 0.2s'
                              _hover={{
                                cursor: 'pointer',
                                bg: 'gray.200',
                                transform: 'translateY(-1px)',
                              }}
                              onClick={() => handleView(content.reference_id)}
                            >
                              <Text fontSize={'sm'} fontWeight={'medium'}>
                                {content.name}
                              </Text>
                              <Text fontSize={'xs'} fontStyle={'italic'}>
                                {content.content_type_name}
                              </Text>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </Box>
              )}

              {!isLoading && !item && (
                <Box textAlign='center' py={10}>
                  <Text>No event data found</Text>
                </Box>
              )}

              {showDeleteModal && (
                <DestructiveModal
                  isOpen={showDeleteModal}
                  onCancel={() => setShowDeleteModal(!showDeleteModal)}
                  onConfirm={() => onDelete()}
                  isLoading={isLoading}
                  title={'Delete Event'}
                  children={`You are about to delete event. Press 'Confirm' to continue.`}
                  confirmText={'Confirm'}
                />
              )}

              {selectedContent && (
                <ContentDrawer {...contentDrawerModal.props} content={selectedContent} />
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}
